import CameraEngine from './camera/CameraEngine'
import { resolveCameraChrome } from './camera/cameraChrome'
import mountCameraViewfinder from './camera/mountViewfinder'
import StagedUpload, { STAGED_UPLOADING, isAbortError, postFormData } from './camera/stagedUpload'
import { rememberThroughput } from './camera/mediaQuality'
import {
  beginCameraClockSession,
  captureStamp
} from './camera/eventClock'

const MODE_STORAGE_PREFIX = 'anear-camera:mode'

function persistCameraMode(eventId, mode) {
  try {
    sessionStorage.setItem(MODE_STORAGE_PREFIX, mode)
    if (eventId) sessionStorage.setItem(`${MODE_STORAGE_PREFIX}:${eventId}`, mode)
  } catch {
    // Private mode / quota — in-memory `_mode` still covers a live session.
  }
}

function readPersistedCameraMode(eventId, enabledModes) {
  try {
    const scoped = eventId ? sessionStorage.getItem(`${MODE_STORAGE_PREFIX}:${eventId}`) : null
    const saved = scoped || sessionStorage.getItem(MODE_STORAGE_PREFIX)
    if (saved && enabledModes.includes(saved)) return saved
  } catch {
    // ignore
  }
  return null
}

/**
 * Unified live Photo | Video camera. Upload and ANEAR_CAPTURE live here.
 * Visible Close / Photo / Video chrome lives on CameraViewfinder.
 *
 * initialize({ onUploadRequest, eventId, onWorkflowStart, onEventClock, publishAction })
 * is idempotent: a later displayRevision only refreshes callbacks.
 *
 * stay-open is an event camera. The live pipeline recocks as soon as the take
 * is queued so the guest's lens and zoom are ready when the upload finishes.
 * The viewfinder keeps that shot on screen and locks the shutter until the POST
 * completes. Close stays locked until the queue drains. One POST at a time.
 *
 * auto-transcode (opt-in): after a successful video POST, enqueue
 * `transcode-workflow` (default "video_transcode") fire-and-forget.
 * Photo takes never start a workflow. Off unless the attr is set.
 */
class AnearCamera extends HTMLElement {
  constructor() {
    super()
    this._onUploadRequest = null
    this._eventId = null
    this._onWorkflowStart = null
    this._onEventClock = null
    this._publishAction = null
    this._viewfinder = null
    this._uploads = []
    this._draining = false
    this._inFlight = false
    this._destroyed = false
    this._clockPromise = null
    this._clockOffsetMs = null
    this._clockSynced = false
    this._mode = null
    this._closedByUser = false
  }

  connectedCallback() {
    if (this._onUploadRequest || this._publishAction) this.openCamera()
  }

  initialize({
    onUploadRequest,
    eventId,
    onWorkflowStart,
    onEventClock,
    publishAction
  } = {}) {
    this._onUploadRequest = onUploadRequest || null
    this._eventId = eventId || null
    this._onWorkflowStart = onWorkflowStart || null
    this._onEventClock = onEventClock || null
    this._publishAction = publishAction || null
    if (this.isConnected) this.openCamera()
  }

  get publishAction() {
    return this._publishAction
  }

  get eventId() {
    return this._eventId
  }

  get stayOpen() {
    const raw = this.getAttribute('stay-open')
    return raw != null && raw !== 'false'
  }

  get facingMode() {
    return this.getAttribute('facing-mode') || 'environment'
  }

  get enabledModes() {
    const raw = (this.getAttribute('modes') || 'photo,video')
      .split(',')
      .map((token) => token.trim())
      .filter((token) => token === 'photo' || token === 'video')
    return raw.length ? [...new Set(raw)] : ['photo', 'video']
  }

  get defaultMode() {
    const requested = this.getAttribute('default-mode') || 'photo'
    return this.enabledModes.includes(requested) ? requested : this.enabledModes[0]
  }

  get currentMode() {
    return this._mode || this.defaultMode
  }

  get purposePhoto() {
    return this.getAttribute('purpose-photo') || this.getAttribute('purpose') || 'tribute_photo'
  }

  get purposeVideo() {
    return this.getAttribute('purpose-video') || this.getAttribute('purpose') || 'tribute_video'
  }

  get maxSeconds() {
    const n = parseInt(this.getAttribute('max-seconds'), 10)
    return Number.isFinite(n) ? n : 20
  }

  get minSeconds() {
    const n = parseInt(this.getAttribute('min-seconds'), 10)
    return Number.isFinite(n) ? n : 3
  }

  get maxBitrate() {
    const n = parseInt(this.getAttribute('max-bitrate'), 10)
    return Number.isFinite(n) ? n : 0
  }

  get actionName() {
    return this.getAttribute('action') || 'ANEAR_CAPTURE'
  }

  get syncClock() {
    return this.getAttribute('sync-clock') !== 'false'
  }

  get autoTranscode() {
    const raw = this.getAttribute('auto-transcode')
    return raw != null && raw !== 'false'
  }

  get transcodeWorkflow() {
    return this.getAttribute('transcode-workflow') || 'video_transcode'
  }

  get chrome() {
    return resolveCameraChrome(this.getAttribute('chrome'), this.getAttribute('camera-style'))
  }

  get inFlight() {
    return this._inFlight
  }

  rememberMode(mode) {
    if (!this.enabledModes.includes(mode)) return
    this._mode = mode
    persistCameraMode(this._eventId, mode)
  }

  setMode(mode) {
    if (!this.enabledModes.includes(mode)) return
    this.rememberMode(mode)
    this._viewfinder?.update({ mode })
  }

  notifyStreamEnded() {
    this.discardStagedMedia()
    this.setInFlight(false)
  }

  setInFlight(value) {
    this._inFlight = !!value
  }

  publish(name, payload) {
    if (this._destroyed || typeof this._publishAction !== 'function') return
    this._publishAction(JSON.stringify({ [name]: payload }))
  }

  handleClose() {
    if (this._inFlight || this._closedByUser) return
    this._closedByUser = true
    this.publish('ANEAR_CAMERA_CLOSED', {})
    // The viewfinder is portaled onto EventFrame, not inside this host. AppM remount
    // is what swaps Capture → Home; do not wait for that to dismiss the overlay.
    this.closeCamera()
  }

  startClockSession() {
    this._clockPromise = null
    this._clockOffsetMs = null
    this._clockSynced = false
    if (!this.syncClock || !this._onEventClock || !this._eventId) return
    this._clockPromise = beginCameraClockSession({
      enabled: true,
      fetchServerNow: () => this._onEventClock({ eventId: this._eventId })
    }).then((session) => {
      this._clockOffsetMs = session.clockOffsetMs
      this._clockSynced = session.synced
      return session
    })
  }

  async stampCapture(deviceNowMs) {
    if (!this.syncClock) return {}
    if (this._clockPromise) {
      try {
        await this._clockPromise
      } catch {
        // ping failure stays unsynced
      }
    }
    return captureStamp({
      enabled: true,
      deviceNowMs,
      clockOffsetMs: this._clockSynced ? this._clockOffsetMs : null
    })
  }

  openCamera() {
    if (this._viewfinder || this._destroyed || this._closedByUser) return
    if (!CameraEngine.isSupported()) return
    this.startClockSession()
    this._mode = readPersistedCameraMode(this._eventId, this.enabledModes) || this.currentMode
    persistCameraMode(this._eventId, this._mode)
    this._viewfinder = mountCameraViewfinder({
      mountFrom: this,
      mode: this._mode,
      modes: this.enabledModes,
      eventId: this._eventId,
      initialFacingMode: this.facingMode,
      stayOpen: this.stayOpen,
      chrome: this.chrome,
      maxSeconds: this.maxSeconds,
      minSeconds: this.minSeconds,
      maxBitrate: this.maxBitrate,
      onCaptured: (media) => this.handleCaptured(media),
      onModeChange: (mode) => this.setMode(mode),
      onClose: () => this.handleClose()
    })
  }

  closeCamera() {
    if (this._viewfinder) {
      this._viewfinder.unmount()
      this._viewfinder = null
    }
  }

  discardStagedMedia() {
    this._uploads.forEach((take) => take.staged?.abort())
    this._uploads = []
    this._draining = false
  }

  captureKind(media) {
    const type = media.type || media.blob?.type || ''
    if (media.durationSecs || type.startsWith('video/')) return 'video'
    return 'photo'
  }

  async handleCaptured(media) {
    if (this._destroyed) return
    if (!this.stayOpen && this._inFlight) return
    if (this.stayOpen && this._uploads.length >= 4) return

    const take = this.createTake(media)
    this._uploads.push(take)
    this.setInFlight(true)
    if (this.stayOpen) this.armLivePreview(take)
    await this.drainUploads()
  }

  createTake(media) {
    const kind = this.captureKind(media)
    const isVideo = kind === 'video'
    const type = media.blob?.type || (isVideo ? 'video/webm' : 'image/jpeg')
    const ext = media.ext || (isVideo ? '.webm' : '.jpg')
    const file = new File([media.blob], `camera-${Date.now()}${ext}`, { type })
    const take = {
      kind,
      width: media.width || null,
      height: media.height || null,
      durationSecs: media.durationSecs || null,
      posterUrl: media.posterUrl || null,
      stamp: this.stampCapture(media.capturedAt || media.startedAt || Date.now()),
      share: null,
      staged: null
    }
    take.staged = new StagedUpload({
      upload: (context) => this.transferMedia(file, { ...context, take }),
      onChange: (state) => {
        take.share = state
        this._viewfinder?.update({ share: this.activeShare() })
      }
    })
    return take
  }

  activeShare() {
    const uploading = this._uploads.find((take) => take.share?.status === STAGED_UPLOADING)
    return (uploading || this._uploads[0])?.share || null
  }

  armLivePreview(take) {
    this._viewfinder?.update({ share: take.share, mode: this.currentMode })
    this._viewfinder?.resetToLive?.()
  }

  async drainUploads() {
    if (this._draining) return
    this._draining = true
    try {
      while (this._uploads.length && !this._destroyed) {
        const take = this._uploads[0]
        try {
          const result = await take.staged.commit()
          if (this._destroyed) return
          this.publishCapture(take, result)
          this._uploads.shift()
          this.finishSuccessfulUpload()
        } catch (error) {
          if (isAbortError(error) || this._destroyed) return
          this._uploads.shift()
          this.setInFlight(this._uploads.length > 0)
          if (this.stayOpen) {
            this._viewfinder?.update({ share: this.activeShare(), mode: this.currentMode })
          }
        }
      }
    } finally {
      this._draining = false
    }
    this.setInFlight(this._uploads.length > 0)
    if (this._uploads.length && !this._destroyed) await this.drainUploads()
  }

  async transferMedia(file, { signal, onProgress, take } = {}) {
    if (!this._onUploadRequest || !this._eventId) {
      throw new Error('Uploader not ready. Try again in a moment.')
    }

    const kind = take?.kind || 'photo'
    const upload = await this._onUploadRequest({
      eventId: this._eventId,
      file,
      purpose: kind === 'video' ? this.purposeVideo : this.purposePhoto
    })
    if (!upload?.uploadUrl || !upload?.finalUrl) {
      throw new Error('Invalid upload response')
    }

    const formData = new FormData()
    const fields = upload.uploadFields || {}
    Object.keys(fields).forEach((key) => formData.append(key, fields[key]))
    formData.append('file', file)

    const startedAt = Date.now()
    await postFormData({ url: upload.uploadUrl, formData, signal, onProgress })
    rememberThroughput({ bytes: file.size, elapsedMs: Date.now() - startedAt })

    const url = upload.finalUrl
    const key = upload.key || upload.s3Key || ''
    if (kind === 'video') this.maybeEnqueueTranscode(take, key, url)

    const stamp = await (take?.stamp || this.stampCapture(Date.now()))
    return {
      url,
      key,
      filename: file.name,
      ...stamp
    }
  }

  // Best-effort: kick the transcode workflow the moment a video clip is in S3.
  // Photo takes never start a workflow. Failures are swallowed; ANAPI → AppM
  // WORKFLOW_NOTIFICATION is the completion path.
  maybeEnqueueTranscode(take, inputKey, inputUrl = '') {
    if (take?.kind !== 'video') return
    if (!this.autoTranscode || !this._onWorkflowStart || !inputKey) return
    const workflow = this.transcodeWorkflow
    const input = { input_key: inputKey, purpose: this.purposeVideo }
    if (inputUrl) input.input_url = inputUrl
    if (this._eventId) input.event_id = this._eventId

    Promise.resolve()
      .then(() => this._onWorkflowStart({ workflow, input }))
      .then((attrs) => {
        this.dispatchEvent(
          new CustomEvent('anear-camera:transcode-enqueued', {
            bubbles: true,
            detail: { workflow, inputKey, inputUrl, runId: attrs?.run_id || attrs?.runId || null }
          })
        )
      })
      .catch(() => {})
  }

  // Never publish `key`. JSAPI treats that field as an action-token auth key
  // and drops the whole ACTION when lookup fails (S3 object keys are not tokens).
  publishCapture(take, result) {
    const payload = {
      kind: take.kind,
      url: result.url,
      filename: result.filename
    }
    if (result.key) payload.s3Key = result.key
    if (result.recordedAt) payload.recordedAt = result.recordedAt
    if (Number.isFinite(result.clockOffsetMs)) payload.clockOffsetMs = result.clockOffsetMs
    if (take.width) payload.width = take.width
    if (take.height) payload.height = take.height
    if (take.durationSecs) payload.durationSecs = take.durationSecs
    if (take.posterUrl) payload.posterUrl = take.posterUrl
    this.publish(this.actionName, payload)
  }

  resetStayOpenSession() {
    this._viewfinder?.update({ share: this.activeShare(), mode: this.currentMode })
    this._viewfinder?.resetToLive?.()
  }

  finishSuccessfulUpload() {
    this.setInFlight(this._uploads.length > 0)
    if (this.stayOpen) {
      this._viewfinder?.update({ share: this.activeShare(), mode: this.currentMode })
      return
    }
    if (!this._uploads.length) this.closeCamera()
  }

  disconnectedCallback() {
    this._destroyed = true
    this.discardStagedMedia()
    this.closeCamera()
  }
}

if (!customElements.get('anear-camera')) {
  customElements.define('anear-camera', AnearCamera)
}

export default AnearCamera
